import { useTranslation } from "react-i18next";
import { createContext, useContext, type ComponentProps, type KeyboardEvent } from "react";
import type { ExtraProps } from "react-markdown";

import { isImageUrl, useImageLinkPreview, useImagePreview } from "@/context/ImagePreview";

// Renderers for the AI reply's markdown `a` / `img`. Both route images to the
// platform's in-app previewer instead of a new tab.

// react-markdown hands every renderer its hast `node`; spread onto a DOM
// element it renders as `node="[object Object]"`.
const omitNode = <P extends ExtraProps>(props: P): Omit<P, "node"> => {
  const rest = { ...props };
  delete rest.node;
  return rest;
};

// True inside MarkdownLink, so an image nested in a link (`[![alt](img)](url)`)
// leaves the click to the link instead of becoming a control inside a control.
const InsideLinkContext = createContext(false);

export const MarkdownLink = (allProps: ComponentProps<"a"> & ExtraProps) => {
  const { href, children, ...props } = omitNode(allProps);
  const previewOnClick = useImageLinkPreview();
  return (
    <a
      className="text-primary underline hover:opacity-80"
      target="_blank"
      rel="noopener noreferrer"
      href={href}
      {...props}
      onClick={(e) => {
        if (href && isImageUrl(href)) previewOnClick(e, [href]);
      }}
    >
      <InsideLinkContext.Provider value={true}>{children}</InsideLinkContext.Provider>
    </a>
  );
};

export const MarkdownImage = (allProps: ComponentProps<"img"> & ExtraProps) => {
  const { src, alt, ...props } = omitNode(allProps);
  const insideLink = useContext(InsideLinkContext);
  const openPreview = useImagePreview();
  const { t } = useTranslation("common");
  const imgProps = { ...props, src, alt: alt ?? "", loading: "lazy" as const };

  if (insideLink || !src) {
    return <img {...imgProps} className="max-w-full rounded-md" />;
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLImageElement>) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    openPreview([src]);
  };

  return (
    <img
      {...imgProps}
      role="button"
      tabIndex={0}
      aria-label={
        alt
          ? t("common:ai_assistant.preview_image_named", { name: alt })
          : t("common:ai_assistant.preview_image")
      }
      className="max-w-full rounded-md cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => openPreview([src])}
      onKeyDown={handleKeyDown}
    />
  );
};
